// --- src/admin/components/Sidebar.jsx ---
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  TagIcon,
  CubeIcon,
  CalendarIcon,
  ReceiptIcon,
  BookmarkIcon,
  CogIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";

const Sidebar = () => {
  // Track which sections are expanded
  const [openSections, setOpenSections] = useState({
    bookings: false,
    categories: false,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <aside className="w-64 p-6 bg-bg text-txt border-r border-border shadow-custom h-full">
      <h2 className="mb-8 text-2xl font-audiowide">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
          Lazer Zone
        </span>
      </h2>
      <nav className="flex flex-col gap-2">
        {/* Dashboard link */}
        <NavLink
          to="/admin"
          className={({ isActive }) =>
            `flex items-center px-4 py-3 rounded-lg transition-all duration-300 hover:bg-hover-bg hover:text-hover-text no-underline ${
              isActive
                ? "bg-hover-bg border-l-4 border-primary font-semibold"
                : "border-transparent"
            }`
          }
        >
          <HomeIcon className="h-5 w-5 mr-3  text-primary " />
          <span>Dashboard</span>
        </NavLink>

        {/* Bookings section */}
        <button
          onClick={() => toggleSection("bookings")}
          className="flex items-center w-full px-4 py-3 rounded-lg justify-between transition-all duration-300 hover:bg-hover-bg hover:text-hover-text no-underline"
        >
          <div className="flex items-center">
            <CalendarIcon className="h-5 w-5 mr-3 text-primary" />
            <span>Bookings</span>
          </div>
          {openSections.bookings ? (
            <ChevronDownIcon className="h-5 w-5 text-txt" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-txt" />
          )}
        </button>
        {openSections.bookings && (
          <div className="ml-8 flex flex-col gap-2">
            <NavLink
              to="/admin/bookings"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              Bookings
            </NavLink>
            <NavLink
              to="/admin/invoices"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              Invoices
            </NavLink>
          </div>
        )}

        {/* Categories section */}
        <button
          onClick={() => toggleSection("categories")}
          className="flex no-underline items-center w-full px-4 py-3 rounded-lg justify-between transition-all duration-300 hover:bg-hover-bg hover:text-hover-text"
        >
          <BookmarkIcon className="h-5 w-5 mr-3 text-primary" />
          <div className="flex-1 flex justify-between items-center">
            <span>Catalog</span>
            {openSections.categories ? (
              <ChevronDownIcon className="h-5 w-5 text-txt" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-txt" />
            )}
          </div>
        </button>
        {openSections.categories && (
          <div className="ml-8  flex flex-col gap-2">
            <NavLink
              to="/admin/games"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              Games
            </NavLink>
            <NavLink
              to="/admin/categories"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              Categories
            </NavLink>
          </div>
        )}
        {/* Customers section */}
        <button
          onClick={() => toggleSection("customers")}
          className="flex items-center w-full px-4 py-3 rounded-lg justify-between transition-all duration-300 hover:bg-hover-bg hover:text-hover-text"
        >
          <UserGroupIcon className="h-5 w-5 mr-3 text-primary" />
          <div className="flex-1 flex justify-between items-center">
          <span>Customers</span>
          {openSections.customers ? (
            <ChevronDownIcon className="h-5 w-5 text-txt" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-txt" />
          )}
          </div>
        </button>
        {openSections.customers && (
          <div className="ml-8 flex flex-col gap-2">
            <NavLink
              to="/admin/users"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              <UsersIcon className="h-5 w-5 mr-2" />
              Customers
            </NavLink>
            <NavLink
              to="/admin/segments"
              className={({ isActive }) =>
                `flex no-underline items-center px-4 py-2 rounded-lg transition-colors duration-300 hover:bg-hover-bg hover:text-hover-text ${
                  isActive ? "bg-hover-bg text-primary font-medium" : "text-txt"
                }`
              }
            >
              <TagIcon className="h-5 w-5 mr-2" />
              Segments
            </NavLink>
          </div>
        )}
        {/* Settings link */}
        <NavLink
          to="/admin/settings"
          className={({ isActive }) =>
            `flex no-underline items-center px-4 py-3 mt-auto rounded-lg transition-all duration-300 hover:bg-hover-bg hover:text-hover-text ${
              isActive
                ? "bg-hover-bg border-l-4 border-primary font-semibold"
                : "border-transparent"
            }`
          }
        >
          <CogIcon className="h-5 w-5 mr-3 text-primary" />
          <span>Settings</span>
        </NavLink>
      </nav>
    </aside>
  );
};

export default Sidebar;
